import { useQuery } from '@tanstack/react-query'

import { consulta, pedir } from '../api/nucleo'

/**
 * La estructura con IA (plan del CMS dinámico §8) en el panel: pedir un plan, seguirlo,
 * aceptarlo con lo que se quita y se renombra, descartarlo y deshacerlo. El panel no decide
 * nada: el núcleo revisa el plan (`revisarPlan`), dice lo que no se aplicará y por qué, y
 * aplica con los permisos de quien acepta. Aquí solo se pinta lo que responde.
 */

export type BloqueDelPlan = { tipo: string; valores: Array<{ campo: string; valor: string }> }
export type CampoDelPlan = { clave: string; etiqueta: string; tipo: string; obligatorio: boolean; campos?: CampoDelPlan[] }
export type Plan = {
  resumen: string
  tipos: Array<{ slug: string; nombre: string; nombrePlural: string; descripcion: string; taxonomias: string[]; conArchivo: boolean; conFicha: boolean; plantillaFicha: BloqueDelPlan[]; plantillaArchivo: BloqueDelPlan[] }>
  taxonomias: Array<{ slug: string; nombre: string; nombrePlural: string; jerarquica: boolean; terminos: Array<{ nombre: string; hijos: string[] }> }>
  gruposDeCampos: Array<{ nombre: string; pestana: string; entidad: string; valor: string; campos: CampoDelPlan[] }>
  paginas: Array<{ slug: string; titulo: string; bloques: BloqueDelPlan[] }>
  menus: Record<'cabecera' | 'legal' | 'pie', Array<{ texto: string; destino: string }>>
  contenidoInicial: Array<{ tipo: string; titulo: string; resumen: string }>
  diseno: { brief: string; vistasPorTipo: string[] }
}

export type EstadoDePlan = 'aceptado' | 'aplicado' | 'aplicando' | 'descartado' | 'deshecho' | 'fallido' | 'pensando' | 'propuesto'

export type VistaDelPlan = {
  id: number
  sitio: number
  origen: 'alta' | 'estructura'
  estado: EstadoDePlan
  instruccion: null | string
  mensaje: null | string
  plan: null | Plan
  recortes: Array<{ ruta: string; motivo: string }>
  diferencias: Array<{ ruta: string; que: 'cambiar' | 'crear'; etiqueta: string; detalle: string[] }>
  pasos: Array<{ clave: string; rotulo: string; estado: 'fallido' | 'hecho' | 'omitido' | 'pendiente'; mensaje?: string }>
  creados: null | { tipos: Record<string, number>; taxonomias: Record<string, number>; grupos: Record<string, number>; paginas: Record<string, number>; contenidos: number[]; terminos: number; avisos: string[] }
  version: null | number
  sePuedeDeshacer: boolean
  /** El nombre en llano de cada tipo de campo («Texto corto», «Número»): el catálogo es del núcleo. */
  tiposDeCampo?: Record<string, string>
  fecha: null | string
  estimado?: number
}

export type Renombre = { nombre?: string; nombrePlural?: string; titulo?: string; etiqueta?: string; texto?: string }
export type Edicion = { quitar: string[]; renombrar: Record<string, Renombre> }

/** Mientras el núcleo trabaja (pensando o aplicando), se vuelve a preguntar cada pocos segundos. */
export const enMarcha = (estado: EstadoDePlan | undefined): boolean => estado === 'pensando' || estado === 'aceptado' || estado === 'aplicando'

export const usePlan = (id: null | number) =>
  useQuery({
    queryKey: ['plan-de-estructura', id],
    enabled: id !== null,
    queryFn: () => pedir<VistaDelPlan>(`/estructura/planes/${id}`),
    refetchInterval: (q) => (enMarcha(q.state.data?.estado) ? 3_000 : false),
  })

export const usePlanes = (sitio: null | number) =>
  useQuery({
    queryKey: ['planes-de-estructura', sitio],
    enabled: sitio !== null,
    queryFn: async () => (await pedir<{ planes: VistaDelPlan[] }>(`/estructura/planes${consulta({ sitio: sitio ?? undefined })}`)).planes,
  })

export type EstimacionDeEstructura = { disponible: boolean; creditos: number; saldo: number; alcanza?: boolean }

export const useEstimacion = (sitio: null | number) =>
  useQuery({
    queryKey: ['estimacion-de-estructura', sitio],
    enabled: sitio !== null,
    queryFn: () => pedir<EstimacionDeEstructura>(`/estructura/planes/estimar${consulta({ sitio: sitio ?? undefined })}`),
  })

export const planes = {
  proponer: (sitio: number, instruccion: string) => pedir<VistaDelPlan>('/estructura/planes/proponer', { metodo: 'POST', cuerpo: { sitio, instruccion } }),
  aceptar: (id: number, edicion: Edicion) => pedir<VistaDelPlan>(`/estructura/planes/${id}/aceptar`, { metodo: 'POST', cuerpo: edicion }),
  descartar: (id: number) => pedir<VistaDelPlan>(`/estructura/planes/${id}/descartar`, { metodo: 'POST' }),
  deshacer: (id: number) => pedir<{ borrados: string[]; conservados: Array<{ que: string; motivo: string }>; completo: boolean; plan: VistaDelPlan }>(`/estructura/planes/${id}/deshacer`, { metodo: 'POST' }),
}
